<script lang="ts">
    import vm, { JourneyNode } from '@journeys/Journeys.viewmodel';
    import { computed, ComputedRef, inject, onMounted, onUnmounted, reactive, ref, watch, WatchStopHandle } from 'vue';

    // utils
    import { cancelFrame, requestFrame } from '@utils';

    // components
    import RecurringEditor from '@cwd/shared/utils/RecuringEditor/RecuringEditor.vue';
    import Notification from '@shared/components/Alerts/Notification.vue';
    import FluidButton from '@shared/components/FluidButton.vue';
    import StepCard from './StepCard.vue';

    import { SegmentDefinition } from '@cwd/Views/Segments/interfaces';
    import { JourneyStatus, NodeTaskVariation, NodeType } from '@journeys/Enums';
    import { debounce, deepClone } from '../../../../shared/utils';
    import ScrollableContainer from '@shared/components/ScrollableContainer.vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SegmentAccess } from '@cwd/Views/Segments/Segments.access';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { UserEventService } from '@shared/Root/UserEvent.service';
    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'Menu_PublishJourney',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const props = defineProps<{}>(),
        { editor, toast, nav, settings, activeJourney, viewport, account } = vm,
        state = reactive({
            reactive_exp: true,
        });

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);
    const userEventService = injectorAccess.injector.get(UserEventService);

    enum SegmentGroupOperator {
        And = 1,
        Or = 2,
    }

    enum JourneyTaskType {
        Issue = 1,
        Survey = 2,
        Sms = 3,
        Event = 4,
        List = 5,
    }

    enum JourneyTaskTypePrefix {
        M = 1,
        F = 2,
        S = 3,
        E = 4,
        L = 5,
    }

    onMounted(() => {
        // serializeStepSegments()
        account.listings.get('/issues/listing');
        account.listings.get('/segments');
    });

    let patienceTimer: NodeJS.Timeout,
        patienceTime = 2500, // 500,
        buttonIsPressed = ref(false),
        progressDone = ref(false);

    //const copyObjects = false
    const publishJourney = async () => {
        editor.history.cache.safeguard = deepClone(activeJourney);

        buttonIsPressed.value = true;

        clearTimeout(patienceTimer);

        patienceTimer = setTimeout(() => {
            buttonIsPressed.value = false;
            progressDone.value = true;

            serializeStepSegments();

            //let toCopy: any = {}
            //editor.segmentGraph.list.forEach((li: EdgeData) => {
            //          if (li.node.data.type !== NodeType.Task || !(li.node.data.IssueId || li.node.data.SmsMessageId || li.node.data.SurveyId)) return
            //	let node = activeJourney.steps[li.node.id]

            //	if (copyObjects) {
            //		if (!activeJourney.ownedObjects.issues[li.node.data.IssueId]) {
            //			let copyIssue = li.node.data.IssueId,
            //				newId = tmp_uuidv4()

            //			toCopy[li.node.id] = copyIssue
            //			node.data.IssueId = newId

            //			activeJourney.steps[li.node.id].data.CopyIssue = copyIssue
            //		} else {
            //			activeJourney.steps[li.node.id].data.CopyIssue = null
            //		}
            //	}
            //})

            //serializeStepSegments()

            //editor.segmentGraph.list.forEach((li: any) => {
            //	if (li.node.data.type !== NodeType.Task) return
            //	let node = activeJourney.steps[li.node.id]

            //	if (toCopy[li.node.id]) {
            //		activeJourney.steps[li.node.id].data.IssueId = node.data.IssueId
            //		activeJourney.steps[li.node.id].data.CopyIssue = toCopy[li.node.id]
            //	} else activeJourney.steps[li.node.id].data.CopyIssue = null
            //})

            userEventService.registerUserEvent('Journey', 'Published journey', null);
            editor.events.emit('re_saveselectedschedule');
        }, patienceTime);

        document.addEventListener(
            'mouseup',
            () => {
                clearTimeout(patienceTimer);
                buttonIsPressed.value = false;
            },
            { once: true }
        );
    };

    const stopJourney = () => {
        activeJourney.Status = JourneyStatus.Draft;
        editor.events.emit('re_disableactiveschedule');
    };

    const buildGraph = (list: any) => {
        editor.segmentGraph.setList(list, activeJourney.startingNode);
        editor.segmentGraph.sequenceSteps();
        activeJourney.steps = editor.segmentGraph.steps;
        activeJourney.startingStep = editor.segmentGraph.startingStep;

        console.debug('Parsed step segments:', activeJourney.steps);
    };

    const getConnections = (obj: any, dir: 'to' | 'fr') =>
        Object.values(obj).reduce((acc: number[], c: any) => {
            let line_conn = activeJourney.lines[c.id][dir];

            line_conn && acc.push(line_conn);

            return acc;
        }, []);

    // TODO: this belongs in graph controller
    const serializeStepSegments = () => {
        if (!activeJourney.startingNode) return;

        const adj_list = new Map(),
            queue_stack: number[] = [],
            newPath = (path_nr: number = path_cnt + 1) => {
                let enum_cnt = [];

                // enum_cnt[NodeType.Placeholder] = 0
                enum_cnt[0] = 0;
                enum_cnt[NodeType.Task] = 0;
                enum_cnt[NodeType.Segment] = 0;
                enum_cnt[NodeType.Condition] = 0;

                return {
                    nr: path_nr,
                    enum_cnt,
                    nodes: {},
                };
            };

        let visited: any = {},
            path_cnt = 0,
            counter: any = [newPath()];

        /**
         * recursive function to serialize node relations
         * @param cid {number} current id
         */
        const nextNode = (cid: number) => {
            if (visited[cid]) {
                queue_stack[0] ? nextNode(queue_stack.shift()!) : buildGraph(adj_list);

                return;
            }

            visited[cid] = true;

            let node = activeJourney.nodes[cid],
                npd = {
                    path: counter[path_cnt].nr,
                    type_nr: ++counter[path_cnt].enum_cnt[node.data.type as 1],
                };

            // node.runtime.ordinal ||= computed(() => {
            // 	let no = `${npd.path}.${npd.type_nr}`

            // 	switch (node.data.type) {
            // 		case NodeType.Task:
            // 			return `M${no}`
            // 		case NodeType.Segment:
            // 			return `L${no}`
            // 		default:
            // 			return
            // 	}
            // })

            let conns_to = getConnections(node.lnto, 'to'),
                conns_fr = getConnections(node.lnfr, 'fr'),
                edge_data = {
                    node,
                    to: conns_to,
                    fr: conns_fr,
                    path: npd.path,
                    type_nr: npd.type_nr,
                };

            adj_list.set(cid, edge_data);

            let ct = conns_to.reduce((a: number[], c: number) => (!visited[c] && a.push(c), a), []);

            if (ct.length > 0) {
                let next = ct.shift();
                queue_stack.push(...ct);
                nextNode(next!);
            } else if (queue_stack[0]) {
                counter[++path_cnt] = newPath();
                nextNode(queue_stack.shift()!);
            } else {
                // end recursion
                Object.keys(activeJourney.nodes).forEach((key: string) => {
                    let node = activeJourney.nodes[key];

                    if (node.data.type !== NodeType.Text) {
                        node = node as JourneyNode;

                        if (!visited[key]) delete node.runtime?.ordinal;
                    }
                });

                buildGraph(adj_list);
            }
        };

        nextNode(activeJourney.startingNode!);
    };

    let bounce = true,
        bounceFrameReq = 0,
        journeyWatcher: WatchStopHandle,
        pageWatcher = watch(
            () => [editor.state.activeMenu, editor.state.showNodeMenu],
            ([activeMenu, nodeMenu]: (string | boolean | null)[]) => {
                if (!nodeMenu && activeMenu === 'publish') {
                    serializeStepSegments();

                    journeyWatcher = watch(
                        [activeJourney.nodes, activeJourney.lines],
                        () =>
                            !editor.state.busy &&
                            debounce(
                                () => {
                                    cancelFrame(bounceFrameReq);
                                    bounceFrameReq = requestFrame(() => {
                                        if ((bounce = !bounce)) serializeStepSegments();
                                    });
                                },
                                { delay: 400 }
                            )
                    );
                } else journeyWatcher && journeyWatcher();
            }
        );

    onUnmounted(() => {
        pageWatcher && pageWatcher();
        journeyWatcher && journeyWatcher();
    });

    // TODO: check for missing condition properties during serialization, ignore disconnected nodes
    /** warnings, errors & info for prep of journey */
    const conditionToasts = computed(() => {
        let toasts = [];
        if (!featureAccess.hasFeature('Automation.Journeys')) {
            toasts.push({
                key: 'missing_feature',
                type: 'error',
                message: {
                    head: $Resources.JourneyMissingFeatureHeading,
                    body: $Resources.JourneyMissingFeatureBody,
                    break: true,
                },
                node_id: null,
            });
        }
        if (!activeJourney.startingNode)
            toasts.push({
                key: 'start_node_err',
                type: 'error',
                message: {
                    head: $Resources.JourneyAlertDefineStartHeading,
                    body: $Resources.JourneyAlertDefineStartBody,
                    break: true,
                },
                node_id: null,
            });

        if (!activeJourney.scheduleController.Scheduled)
            toasts.push({
                key: 'start_date_warn',
                type: 'warning',
                message: {
                    head: $Resources.JourneyAlertNotScheduledHeading,
                    body: $Resources.JourneyAlertNotScheduledBody,
                    break: true,
                },
                node_id: null,
            });

        if (toasts.length >= 1) return toasts;

        let nodes = activeJourney.nodes;
        for (let key in nodes) {
            let node = nodes[key];
            if (node.data.type === NodeType.Text) continue;

            if (node.data.type === NodeType.Segment) {
                if (node.data.ListIds?.length === 0 && node.data.SegmentIds?.length === 0) {
                    toasts.push({
                        key: 'start_segment_null',
                        type: 'error',
                        message: {
                            head: `'${node.data.title}' is missing a list or segment`,
                            body: `At least one list or segment has to be set.`,
                            break: true,
                        },
                        node_id: node.id,
                    });
                }
            }
            // else if (account.lists.contactLists[node.data.startingSegment.ListId]?.ContactCount === 0)
            //    toasts.unshift({
            //       key: 'start_segment_empty',
            //       type: 'warning',
            //       message: {
            //          head: 'Starting segment has no contacts.'
            //       },
            //       node_id: node.id
            //    })
            if (node.data.type === NodeType.Task) {
                if (node.data.var === NodeTaskVariation.Event) {
                    if (!node.data.EventId) {
                        toasts.push({
                            key: `${node.id}_task_node_event_null`,
                            type: 'warning',
                            message: {
                                head: `'${node.data.title}' is missing an event.`,
                                body: 'You must choose an event for this step.',
                            },
                            node_id: node.id,
                        });
                    }
                } else if (!node.data.IssueId && !node.data.SmsMessageId) {
                    toasts.push({
                        key: `${node.id}_task_node_issue_null`,
                        type: 'warning',
                        message: {
                            head: `'${node.data.title}' is missing issue or sms.`,
                            body: 'You must choose an issue or sms for this step.',
                        },
                        node_id: node.id,
                    });
                }
            }
        }

        stepList.value?.forEach((step: any) => {
            const isInvalid = collectIsValid(step.data.SegmentDefinition).includes(false);
            if (isInvalid) {
                toasts.push({
                    key: `${step.data.Name}_step_invalid`,
                    type: 'error',
                    message: {
                        head: $Resources.JourneyInvalidSegmentHead,
                        body: $Resources.JourneyInvalidSegmentBody,
                    },
                    node_id: step.nodeId,
                });
            }
        });

        return toasts;
    });

    function collectIsValid(segmentDefinition: SegmentDefinition | undefined) {
        if (!segmentDefinition) return [];
        let isValidValues: boolean[] = [];

        if (segmentDefinition.Type !== 'SegmentGroup') {
            isValidValues.push(segmentDefinition.IsValid === undefined ? true : (segmentDefinition.IsValid ?? false));
        }

        if (segmentDefinition.Type === 'SegmentGroup' && segmentDefinition.Segments?.length) {
            for (const subSegment of segmentDefinition.Segments) {
                isValidValues = isValidValues.concat(collectIsValid(subSegment));
            }
        }
        return isValidValues;
    }

    const inspectToastTarget = (id: number) => {
        editor.state.nodeSettingsTab = 'data';
        editor.state.showNodeMenu = true;
        editor.tools.sel_keyObj = null;
        editor.tools.sel_nodes = {};
        editor.tools.sel_nodes[id] = activeJourney.nodes[id];
        editor.tools.sel_points = {};
        editor.tools.sel_lines = {};
        editor.tools.highlight = {};

        // not uber precise .. 120 is for half of propWindow
        nav.x = editor.tools.sel_nodes[id].x + viewport.computed.value.raw_pws / 2 + editor.tools.sel_nodes[id].w / 2;
        nav.y = editor.tools.sel_nodes[id].y + editor.tools.sel_nodes[id].h / 2;
    };

    const disableNodeSchedules = async (callback: <A, T>(...args: A[] | any) => T | any) => {
        let steps: any = Object.values(activeJourney.steps),
            objectIds = Array(steps.length);

        //activeJourney.scheduleController.Scheduled = null

        for (let i = 0; i < steps.length; i++) {
            let node = activeJourney.nodes[steps[i].nodeId] as JourneyNode;

            if (!node || !node.data.Schedule) continue;

            node.data.Schedule!.Scheduled = null;
            node.data.Schedule!.TaskStatus = 'Aborted';
            objectIds[i] = steps[i].data.IssueId || steps[i].data.SmsMessageId;
        }

        await callback<string[], void>(objectIds);

        toast.emit('note', { pos: 'top-center', type: 'success', message: { head: 'Journey stopped.', body: 'That is all.', break: true } });
        editor.events.emit('savecurrent');
    };

    const handleResponse = async (data: any, callback: <A, T>(...args: A[] | any) => T | any) => {
        if (data === null) return disableNodeSchedules(callback);

        activeJourney.Status = JourneyStatus.Active;
        // setPageState('steps')

        let objectIds = Array(data.length);

        for (let i = 0; i < data.length; i++) {
            let node = activeJourney.nodes[data[i].NodeId] as JourneyNode;

            node.data.Schedule = data[i].Schedule;
            node.data.TaskId ||= data[i].TaskId;

            if (data[i].IssueId) objectIds[i] = node.data.IssueId = data[i].IssueId;
            else if (data[i].SmsMessageId) objectIds[i] = node.data.SmsMessageId = data[i].SmsMessageId;
        }

        await callback<string[], void>(objectIds);

        toast.emit('note', { pos: 'top-center', type: 'success', message: { head: 'Wohoo', body: 'Journey scheduled.', break: true } });
        editor.events.emit('savecurrent');
    };

    const stepList: ComputedRef<any[] | null> = computed(() => {
        let steps: any[] = Object.values(activeJourney.steps);

        if (typeof steps[0] !== 'object') {
            serializeStepSegments();
            steps = Object.values(activeJourney.steps);
        }

        return steps.sort((a: any, b: any) => a.delay - b.delay);
    });

    const triggerState = () => (state.reactive_exp = !state.reactive_exp);
    editor.events.addListener('triggerstate', triggerState);

    const pageState = ref('steps');
    const setPageState = (state: string, callback?: Func) => {
        if (state === 'schedule') {
            editor.events.emit('re_croneditor', 'edit', activeJourney.scheduleController);
        } else {
            editor.events.emit('triggerstate');
        }

        callback && callback();

        pageState.value = state;
    };
</script>

<template>
    <ScrollableContainer>
        <h2 class="h3">{{ $Resources.ScheduleJourneyHeading }}</h2>

        <RecurringEditor v-model:start="activeJourney.scheduleController.Scheduled"
                         :ownerId="activeJourney.JourneyId!"
                         :disabled="activeJourney.Status === JourneyStatus.Active"
                         :ownerName="activeJourney.Title"
                         :scheduleController="activeJourney.scheduleController"
                         :steps="stepList"
                         :scheduleCallback="handleResponse"
                         :handleSchedule="() => setPageState('schedule')" />

        <div v-if="pageState === 'schedule'" class="form-group d-flex gap-sm flex-wrap">
            <FluidButton :text="$Resources.CloseButton"
                         class="btn btn-subtle"
                         @click="setPageState('steps', () => {
                    editor.events.emit('re_croneditor', 'save');
                    editor.events.emit('re_croneditor', 'close');
                })"></FluidButton>
        </div>

        <template v-if="pageState === 'steps'">
            <div>
                <transition-group name="cards">
                    <Notification v-for="({ key, type, message, node_id }, idx) of conditionToasts"
                                  :key="key"
                                  :style="`--idx: ${idx}`"
                                  :type="type"
                                  class="static"
                                  :message="message"
                                  sizing="small"
                                  :inspect="node_id ? () => inspectToastTarget(node_id) : undefined" />

                    <Notification v-if="conditionToasts.length === 0 && Object.keys(activeJourney.steps).length === 0"
                                  type="success"
                                  class="static"
                                  :message="{ head: 'All set!', body: 'You are ready to start this journey.', break: true }"
                                  sizing="small" />
                </transition-group>
            </div>

            <StepCard v-for="step of stepList" :key="step.data.Name + step.chain.flat().join('-')" :step="step" />
        </template>
    </ScrollableContainer>

    <div class="static-container bottom">
        <Notification v-if="activeJourney.Status === JourneyStatus.Template"
                      type="error"
                      class="static"
                      :message="{ head: $Resources.ErrorTitleLabel, body: `[You can't publish a template.]`, break: true }"
                      sizing="small" />

        <div class="no-fl-gs mt-xs p-relative">
            <FluidButton v-if="activeJourney.Status === JourneyStatus.Draft"
                         class="btn btn-cta btn-block"
                         :disabled="(conditionToasts.length !== 0 && 'inactive') || progressDone"
                         :text="activeJourney.startingNode ? $Resources.JourneyStartLabel : $Resources.JourneyNotReadyLabel"
                         @mousedown.left="publishJourney" />

            <span class="btn-hold-progress p-absolute" :class="{ active: buttonIsPressed, done: progressDone }"></span>

            <FluidButton v-if="activeJourney.Status === JourneyStatus.Active"
                         class="btn btn-cta btn-block"
                         :text="$Resources.JourneyStopLabel"
                         @mousedown.left="stopJourney" />
        </div>
    </div>
</template>

<style lang="less">
    .btn-hold-progress {
        transition: opacity 250ms ease-in-out;
        opacity: 0;
        background-color: var(--brand-secondary-100);
        border-radius: var(--border-radius);
        right: 0;
        bottom: -10px;
        left: 0;
        overflow: hidden;
        &:after

    {
        content: '';
        display: block;
        width: 0%;
        height: 5px;
        background-color: #90d850;
        box-shadow: 1px 0 0 rgba(255, 255, 255, 0.25);
        // transition: width 250ms ease-in-out;
    }

    &.active,
    &.done {
        &:after

    {
        width: 100%;
        transition: width 2500ms ease-in-out;
    }

    }
    }

    .btn:hover + .btn-hold-progress,
    .btn-hold-progress.done {
        opacity: 1;
    }

    .step-container {
        padding: 0.938rem;
        border-radius: var(--border-radius);
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius);
        margin-top: 0.4rem;
        // box-shadow: 0 0.2rem 0.8rem -0.3rem #00000014;
        transition: border-color, box-shadow, outline;
        transition-duration: 0.2s;
        transition-timing-function: ease;
    }

        .step-container:hover {
            // box-shadow: 0 6px 18px -6px #00000054;
            border: 1px solid var(--brand-primary-500);
        }

    .notification-wrapper {
        --toast-height: 200px;
    }

    .cards-enter-active,
    .cards-leave-active {
        transition-property: max-height transform opacity;
        transition-duration: 1s, 0.6s, 0.5s;
        transition-timing-function: ease-out;
        transition-delay: calc(var(--idx) * 0.25s);
    }

    .cards-enter-from {
        max-height: var(--toast-height);
        /* transform: translate3d(0, 12rem, 0); */
        opacity: 0;
    }

    .cards-enter-to {
        max-height: var(--toast-height);
        opacity: 1;
    }

    .cards-leave-from {
        max-height: var(--toast-height);
        opacity: 1;
    }

    .cards-leave-to {
        max-height: 0;
        transform: scale(0.85);
        opacity: 0;
    }

    .card-section.fl {
        display: flex;
        flex-direction: row;
    }

    .bb {
        border-bottom: 0.1rem solid rgb(211, 196, 196);
    }

    .task-wrapper {
        width: 100%;
        height: 4.5rem;
        margin-top: 1rem;
    }

    .task {
        display: flex;
        position: relative;
        width: 100%;
        align-items: center;
        justify-content: center;
        flex-direction: column;
    }

    .task-progress {
        position: relative;
        height: 0.5rem;
        width: 96%;
        border-radius: 0.3rem;
        outline: 0.2rem solid rgb(142 1 95);
        outline-offset: -0.1rem;
        box-shadow: 0 0.3rem 0.3rem 0.1rem rgb(0 58 58 / 36%);
    }

        .task-progress:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 37%;
            background-color: rgb(137 1 92);
            z-index: 1;
        }

    .task-wrapper .subheading {
        padding-bottom: 0.2rem;
    }

    .no-fl-gs {
        flex-grow: 0;
        flex-shrink: 0;
    }

    .icon-container {
        flex-shrink: 0;
        flex-grow: 0;
        height: 1.5rem;
        width: 1.5rem;
        margin-right: 0.6rem;
        background-color: rgb(180, 52, 180);
        border-radius: 1.5rem;
        outline: 0.3rem solid rgba(32, 21, 6, 0.137);
        outline-offset: -0.1rem;
    }

    .card.split {
        display: flex;
        flex-direction: row;
    }

    .graph {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30%;
        background-color: rgb(31, 97, 91);
        border-radius: 0.3rem;
        margin-right: 0.6rem;
    }

    .card.rows {
        display: flex;
        flex-direction: column-reverse;
    }

        .card.rows .graph {
            height: 2.5rem;
            width: 100%;
            /* margin-top: 0.6rem; */
        }
</style>
